/* ==========================================================================
   Maison Carthia - Le Ruban (F6)
   Homepage-only sticky Discovery ribbon behaviour. Self-contained, no
   dependencies, no cart interaction, no pub/sub.

   - Shows once the trust strip (.mc-trust-strip) scrolls out of view.
     Fallback if that section is absent: show after ~1 viewport of scroll.
   - Hides while the newsletter (.mc-nl) or footer (.footer) is in view.
   - Past ~70% scroll depth with no CTA click, swaps to the quiz state (one-way).
   - Dismiss (X) suppresses BOTH states for the rest of the session.
   - Reduced motion is handled in CSS; JS only toggles classes/attributes.
   ========================================================================== */
(function () {
  'use strict';

  var STORAGE_KEY = 'mc-ruban-dismissed';
  var SWAP_DEPTH = 0.7;

  var root = document.getElementById('mc-ruban');
  if (!root || root.dataset.mcRubanReady === '1') {
    return;
  }
  root.dataset.mcRubanReady = '1';

  var setRow = root.querySelector('[data-mc-ruban-row="set"]');
  var quizRow = root.querySelector('[data-mc-ruban-row="quiz"]');

  var designMode = !!(window.Shopify && window.Shopify.designMode);

  // ---- session dismissal (private-mode safe) --------------------------------
  function isDismissed() {
    try {
      return sessionStorage.getItem(STORAGE_KEY) === '1';
    } catch (e) {
      return false;
    }
  }
  function storeDismissed() {
    try {
      sessionStorage.setItem(STORAGE_KEY, '1');
    } catch (e) {
      /* degrade to per-pageview */
    }
  }

  // If the visitor dismissed earlier this session, leave the ribbon hidden.
  if (!designMode && isDismissed()) {
    return;
  }

  // Reveal the element to CSS (still invisible until .is-visible).
  root.removeAttribute('hidden');

  // ---- state ----------------------------------------------------------------
  var passedTrust = false;
  var endInView = false;
  var swapped = false;
  var ctaClicked = false;
  var dismissed = false;

  function update() {
    root.classList.toggle('is-visible', passedTrust && !endInView && !dismissed);
  }

  // ---- dismiss --------------------------------------------------------------
  function dismiss() {
    dismissed = true;
    if (!designMode) {
      storeDismissed();
    }
    // Move focus out of the ribbon before it hides, but only if it currently
    // holds focus (so a mouse click never yanks a keyboard user elsewhere).
    // #MainContent is Dawn's skip-link target (tabindex="-1"), always present.
    if (root.contains(document.activeElement)) {
      var main = document.getElementById('MainContent');
      if (main && typeof main.focus === 'function') {
        main.focus();
      }
    }
    update();
    // Re-hide after the fade so nothing stays focusable.
    window.setTimeout(function () {
      if (dismissed) {
        root.setAttribute('hidden', '');
      }
    }, 300);
  }

  var dismissButtons = root.querySelectorAll('[data-mc-ruban-dismiss]');
  for (var i = 0; i < dismissButtons.length; i++) {
    dismissButtons[i].addEventListener('click', function (evt) {
      evt.preventDefault();
      dismiss();
    });
  }

  // Note the CTA click so the quiz swap never overrides an interested visitor.
  var ctaLinks = root.querySelectorAll('[data-mc-ruban-cta]');
  for (var c = 0; c < ctaLinks.length; c++) {
    ctaLinks[c].addEventListener('click', function () {
      ctaClicked = true;
    });
  }

  // ---- design mode: show immediately, skip scroll logic ---------------------
  if (designMode) {
    passedTrust = true;
    update();
    return;
  }

  // ---- quiz swap ------------------------------------------------------------
  function swapToQuiz() {
    if (swapped || ctaClicked || !setRow || !quizRow) {
      return;
    }
    swapped = true;
    // If focus was parked on a control in the set row, this scroll-triggered
    // swap would drop it to <body>. Move it to the quiz CTA that visually
    // replaces it, but only when the ribbon actually held focus, and only
    // AFTER the quiz row is revealed (you cannot focus a display:none element).
    var hadFocus = setRow.contains(document.activeElement);
    setRow.setAttribute('hidden', '');
    quizRow.removeAttribute('hidden');
    if (hadFocus) {
      var nextTarget =
        quizRow.querySelector('[data-mc-ruban-cta]') ||
        quizRow.querySelector('[data-mc-ruban-dismiss]');
      if (nextTarget && typeof nextTarget.focus === 'function') {
        nextTarget.focus();
      }
    }
  }

  // ---- show trigger: IntersectionObserver on the trust strip ----------------
  var trustEl = document.querySelector('.mc-trust-strip');
  var useScrollFallback = !trustEl;

  if (trustEl && 'IntersectionObserver' in window) {
    var showObserver = new IntersectionObserver(function (entries) {
      for (var e = 0; e < entries.length; e++) {
        var entry = entries[e];
        // Passed once the strip has scrolled up off the top of the viewport.
        passedTrust = !entry.isIntersecting && entry.boundingClientRect.top < 0;
      }
      update();
    }, { threshold: 0 });
    showObserver.observe(trustEl);
  } else {
    useScrollFallback = true;
  }

  // ---- hide trigger: newsletter + footer ------------------------------------
  var endTargets = document.querySelectorAll('.mc-nl, .footer');
  if (endTargets.length && 'IntersectionObserver' in window) {
    var inView = [];
    var hideObserver = new IntersectionObserver(function (entries) {
      for (var e = 0; e < entries.length; e++) {
        var entry = entries[e];
        var idx = inView.indexOf(entry.target);
        if (entry.isIntersecting && idx === -1) {
          inView.push(entry.target);
        } else if (!entry.isIntersecting && idx !== -1) {
          inView.splice(idx, 1);
        }
      }
      endInView = inView.length > 0;
      update();
    }, { threshold: 0 });
    for (var t = 0; t < endTargets.length; t++) {
      hideObserver.observe(endTargets[t]);
    }
  }

  // ---- scroll: quiz-swap depth (+ trust fallback) ---------------------------
  var ticking = false;
  function onScroll() {
    if (ticking) {
      return;
    }
    ticking = true;
    window.requestAnimationFrame(function () {
      ticking = false;

      if (useScrollFallback) {
        passedTrust = window.scrollY > window.innerHeight;
      }

      var docEl = document.documentElement;
      var scrollable = docEl.scrollHeight - window.innerHeight;
      var depth = scrollable > 0
        ? (window.scrollY + window.innerHeight) / docEl.scrollHeight
        : 0;
      if (depth >= SWAP_DEPTH) {
        swapToQuiz();
      }

      if (useScrollFallback) {
        update();
      }
    });
  }

  window.addEventListener('scroll', onScroll, { passive: true });
  onScroll();
})();
